import { Injectable } from '@angular/core';
import { ProjectMemberInfo } from './voice-context.service';

export interface MemberMatch {
  memberId: string | null;
  memberName: string;
  confidence: 'exact' | 'partial' | 'none';
}

/**
 * Service de resolution de noms pour les commandes vocales
 * Transforme un nom prononce en profile_id
 */
@Injectable({ providedIn: 'root' })
export class MemberResolverService {

  /**
   * Resout un nom vers un membre du projet
   * Algorithme: exact full name > reversed name > partial unique match
   */
  resolve(name: string, members: ProjectMemberInfo[]): MemberMatch {
    if (!name || members.length === 0) {
      return { memberId: null, memberName: name, confidence: 'none' };
    }

    const normalized = name.toLowerCase().trim();

    // 1. Match exact nom complet (prenom nom ou nom prenom)
    for (const m of members) {
      const fullName = `${m.firstName} ${m.lastName}`.toLowerCase();
      const reverseName = `${m.lastName} ${m.firstName}`.toLowerCase();

      if (normalized === fullName || normalized === reverseName) {
        return {
          memberId: m.profileId,
          memberName: `${m.firstName} ${m.lastName}`,
          confidence: 'exact'
        };
      }
    }

    // 2. Match exact email (peu probable en vocal mais supporté)
    for (const m of members) {
      if (m.email.toLowerCase() === normalized) {
        return {
          memberId: m.profileId,
          memberName: `${m.firstName} ${m.lastName}`,
          confidence: 'exact'
        };
      }
    }

    // 3. Match partiel unique (prenom OU nom contenu, > 2 chars)
    const partialMatches = members.filter(m => {
      const fn = m.firstName.toLowerCase();
      const ln = m.lastName.toLowerCase();
      return (fn.length > 2 && normalized.includes(fn))
        || (ln.length > 2 && normalized.includes(ln));
    });

    if (partialMatches.length === 1) {
      const m = partialMatches[0];
      return {
        memberId: m.profileId,
        memberName: `${m.firstName} ${m.lastName}`,
        confidence: 'partial'
      };
    }

    return { memberId: null, memberName: name, confidence: 'none' };
  }
}